import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Search,
  Phone,
  Mail,
  MessageSquare,
  User,
  Eye,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  X,
  CheckSquare
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Button, LoadingSpinner } from './components';
import RequestDetailModal from './RequestDetailModal';

function RequestsPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'active');
  const [sourceFilter, setSourceFilter] = useState('all');
  // Default filter: show new + follow_up (active leads)
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const itemsPerPage = 50;

  // API state
  const [requestsData, setRequestsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [interactionsMap, setInteractionsMap] = useState({});

  // State for server-side pagination
  const [totalCount, setTotalCount] = useState(0);
  const [serverPage, setServerPage] = useState(1);

  // State for status counts (from API, not calculated from paginated data)
  const [statusCounts, setStatusCounts] = useState({
    total: 0, new: 0, follow_up: 0, snoozed: 0, converted: 0, lost: 0, existing_client_request: 0, resolved: 0
  });

  // Inline action popup state
  const [actionPopup, setActionPopup] = useState(null); // { id, type }
  const [snoozeDate, setSnoozeDate] = useState('');
  const [snoozeComment, setSnoozeComment] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [actionProcessing, setActionProcessing] = useState(false);

  // Fetch interactions from API
  const fetchInteractions = async (page = 1) => {
    console.log('%c🔥 REQUESTS PAGE LOADED - NEW BUILD', 'background: #222; color: #bada55; font-size: 20px');
    try {
      setLoading(true);
      setError(null);

      // Build params for server-side filtering
      const params = { page, page_size: 50, exclude_internal: 'true' };
      if (statusFilter === 'active') {
        params.status__in = 'new,follow_up,existing_client_request';
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Send tab filter server-side
      if (activeTab === 'google') {
        params.source_category = 'google_ads';
      } else if (activeTab !== 'all') {
        params.type_category = activeTab;
      }
      if (sourceFilter !== 'all') {
        params.source_category = sourceFilter;
      }

      const response = await apiService.getInteractions(params);
      const data = response.results || response || [];
      setTotalCount(response.count || data.length);

      // Set status counts from API response (accurate totals across all pages)
      if (response.status_counts) {
        setStatusCounts(response.status_counts);
      }

      // Store raw interactions in a map for modal access
      const interactionsById = {};
      data.forEach(interaction => {
        interactionsById[interaction.id] = interaction;
      });
      setInteractionsMap(interactionsById);

      // Helper function to get user-friendly type label
      const getTypeLabel = (type) => {
        const typeLabels = {
          'google_ads_phone_call': 'Google Ads Call',
          'google_ads_phone_text': 'Google Ads Text',
          'google_ads_call': 'Google Ads Call',
          'phone_text': 'Phone/Text',
          'phone_call': 'Phone Call',
          'call': 'Call',
          'sms': 'SMS',
          'email': 'Email',
          'google_lead': 'Google Lead',
          'google_ads_lead': 'Google Ads Lead',
          'website_message': 'Website Message',
          'online_booking': 'Online Booking',
          'other': 'Other'
        };
        return typeLabels[type] || (type || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };

      // Helper function to get simple type for filtering/tabs
      const getSimpleType = (type) => {
        if (!type) return 'other';
        if (type.includes('call')) return 'call';
        if (type.includes('text') || type.includes('sms')) return 'message';
        if (type.includes('lead')) return 'lead';
        if (type.includes('email')) return 'message';
        if (type.includes('message')) return 'message';
        return 'other';
      };

      // Helper function to get source category
      const getSourceCategory = (type, metadata) => {
        if (!type) return 'other';
        if (type.includes('google_ads')) return 'google_ads';
        if (metadata?.ad_source === 'google_ads' || metadata?.utm?.utm_source) return 'google_ads';
        if (type.includes('website')) return 'website';
        return 'direct';
      };

      // Helper function to create subject from content
      const getSubject = (interaction) => {
        const content = interaction.content || '';
        if (content && content.length > 0) {
          // Extract first line or first 60 characters
          const firstLine = content.split('\n')[0].trim();
          return firstLine.length > 60 ? firstLine.substring(0, 60) + '...' : firstLine;
        }
        return 'Inquiry';
      };

      // Transform API data to match the component's expected format
      const transformedData = data.map(interaction => {
        // Get name or fallback to phone number (like production)
        let displayName = 'Unknown';
        if (interaction.person) {
          const fullName = `${interaction.person.first_name || ''} ${interaction.person.last_name || ''}`.trim();
          if (fullName) {
            displayName = fullName;
          } else if (interaction.person.phone_number) {
            displayName = interaction.person.phone_number;
          }
        }

        const metadata = interaction.metadata || {};
        const isGoogleAd = metadata.ad_source === 'google_ads' || metadata.utm?.utm_source === 'google' || (interaction.type || '').includes('google_ads');
        const typeLabel = isGoogleAd && !interaction.type?.includes('google_ads') ? 'Google Ads Lead' : getTypeLabel(interaction.type);
        const simpleType = getSimpleType(interaction.type);
        const sourceCategory = getSourceCategory(interaction.type, metadata);

        return {
          id: interaction.id,
          type: simpleType,  // Simplified type for filtering
          typeLabel: typeLabel,  // User-friendly display label
          rawType: interaction.type,  // Original API type
          source: typeLabel,  // Use the same user-friendly label for source
          sourceCategory: sourceCategory,  // For filtering: google_ads, website, direct
          isGoogleAd: isGoogleAd,
          contact: displayName,
          email: interaction.person?.email || 'N/A',
          phone: interaction.person?.phone_number || 'N/A',
          location: 'N/A', // Not available in API data
          subject: getSubject(interaction),
          content: interaction.content || 'No content',
          transcript: interaction.transcript || null,
          status: interaction.status || 'new',
          createdAt: interaction.date_time,
          duration: interaction.metadata?.duration || null,
          isExistingClient: interaction.is_existing_client || false,
          snoozeUntil: interaction.snooze_until || null,
          snoozeComment: interaction.snooze_comment || null,
          lostReason: interaction.lost_reason || null,
          conversion: interaction.conversion || null,
        };
      });

      setRequestsData(transformedData);
    } catch (err) {
      setError(err.message || 'Failed to fetch interactions');
      console.error('Error fetching interactions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInteractions(serverPage);
  }, [serverPage, statusFilter, activeTab, sourceFilter]);

  // Handler to open interaction detail modal
  const handleViewDetails = (requestId) => {
    console.log('=== CLICK DEBUG ===');
    console.log('Clicked request ID:', requestId);
    console.log('Interactions map:', interactionsMap);
    console.log('Available IDs:', Object.keys(interactionsMap));
    const interaction = interactionsMap[requestId];
    console.log('Found interaction:', interaction);
    if (interaction) {
      console.log('Setting selected interaction');
      setSelectedInteraction(interaction);
    } else {
      console.error('No interaction found for ID:', requestId);
    }
  };

  // Inline action handlers
  const openActionPopup = (id, type) => {
    setActionPopup({ id, type });
    setSnoozeDate('');
    setSnoozeComment('');
    setLostReason('');
  };

  const closeActionPopup = () => {
    setActionPopup(null);
    setSnoozeDate('');
    setSnoozeComment('');
    setLostReason('');
  };

  const executeAction = async () => {
    if (!actionPopup) return;
    const { id, type } = actionPopup;
    setActionProcessing(true);
    try {
      if (type === 'snooze') {
        if (!snoozeDate) return;
        await apiService.snoozeInteraction(id, snoozeDate, snoozeComment);
      } else if (type === 'resolve') {
        await apiService.resolveInteraction(id);
      } else if (type === 'converted') {
        await apiService.markInteractionAsConverted(id);
      } else if (type === 'lost') {
        await apiService.markInteractionAsLost(id, lostReason);
      } else if (type === 'delete') {
        await apiService.delete(`/amn/interactions/${id}/`);
      }
      closeActionPopup();
      fetchInteractions(serverPage);
    } catch (err) {
      console.error('Failed to execute action:', err);
    } finally {
      setActionProcessing(false);
    }
  };

  // Handler to refresh data after modal updates
  const handleModalUpdate = () => {
    fetchInteractions();
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = requestsData.filter(request => {
      const matchesSearch =
        request.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (request.transcript && JSON.stringify(request.transcript).toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesType = typeFilter === 'all' || request.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || statusFilter === 'active'
        ? (statusFilter === 'active' ? ['new', 'follow_up', 'existing_client_request'].includes(request.status) : true)
        : request.status === statusFilter;
      const matchesSource = sourceFilter === 'all' || request.sourceCategory === sourceFilter;

      const matchesTab = activeTab === 'all' || (activeTab === 'google' ? request.sourceCategory === 'google_ads' : request.type === activeTab);

      return matchesSearch && matchesType && matchesStatus && matchesSource && matchesTab;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'createdAt' || sortField === 'lastActivity') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [requestsData, searchTerm, typeFilter, statusFilter, sourceFilter, activeTab, sortField, sortDirection]);

  // Server-side pagination (50 per page)
  const serverTotalPages = Math.ceil(totalCount / 50);
  // Client-side pagination within the fetched page
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredAndSortedData.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800 border border-blue-300';
      case 'follow_up': return 'bg-purple-100 text-purple-800 border border-purple-300';
      case 'snoozed': return 'bg-yellow-100 text-yellow-800 border border-yellow-300';
      case 'converted': return 'bg-green-100 text-green-800 border border-green-300';
      case 'existing_client_request': return 'bg-teal-100 text-teal-800 border border-teal-300';
      case 'resolved': return 'bg-gray-100 text-gray-600 border border-gray-300';
      case 'lost': return 'bg-red-100 text-red-800 border border-red-300';
      default: return 'bg-gray-100 text-gray-700 border border-gray-300';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'message': return <Mail className="w-3.5 h-3.5" />;
      case 'call': return <Phone className="w-3.5 h-3.5" />;
      case 'lead': return <User className="w-3.5 h-3.5" />;
      default: return <MessageSquare className="w-3.5 h-3.5" />;
    }
  };

  const getTypeColor = (type) => {
    // All types use consistent gray color
    return 'text-gray-600';
  };



  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const date = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const time = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date}, ${time}`;
  };

  // Use status counts from API (accurate totals across all pages)
  const totalRequests = statusCounts.total || totalCount;
  const newRequests = statusCounts.new || 0;
  const followUpRequests = statusCounts.follow_up || 0;
  const snoozedRequests = statusCounts.snoozed || 0;
  const convertedRequests = statusCounts.converted || 0;
  const lostRequests = statusCounts.lost || 0;
  const existingClientRequests = statusCounts.existing_client_request || 0;
  const resolvedRequests = statusCounts.resolved || 0;

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Page Title */}
      <div className="mb-2 flex justify-between items-center">
        <h1 className="text-lg font-semibold text-gray-900">Requests</h1>
        <div className="text-xs text-gray-600">
          {totalRequests} total · {newRequests} new · {followUpRequests} follow-up · {snoozedRequests} snoozed · {convertedRequests} converted · {existingClientRequests} existing · {resolvedRequests} resolved · {lostRequests} lost
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <Panel className="p-6">
          <LoadingSpinner />
        </Panel>
      )}

      {/* Error State */}
      {error && (
        <Panel className="bg-red-50 border-red-200 mb-2">
          <div className="flex items-center">
            <div className="text-xs font-medium text-red-800">Error: {error}</div>
          </div>
        </Panel>
      )}

      {/* Tabs */}
      <Panel noPadding className="mb-2">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-1 px-2 pt-2">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-1.5 px-3 border-b-2 font-medium text-xs transition-colors ${
                activeTab === 'all'
                  ? 'border-red-800 text-red-800'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('message')}
              className={`py-1.5 px-3 border-b-2 font-medium text-xs transition-colors ${
                activeTab === 'message'
                  ? 'border-red-800 text-red-800'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              Messages
            </button>
            <button
              onClick={() => setActiveTab('call')}
              className={`py-1.5 px-3 border-b-2 font-medium text-xs transition-colors ${
                activeTab === 'call'
                  ? 'border-red-800 text-red-800'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              Calls
            </button>
            <button
              onClick={() => setActiveTab('lead')}
              className={`py-1.5 px-3 border-b-2 font-medium text-xs transition-colors ${
                activeTab === 'lead'
                  ? 'border-red-800 text-red-800'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              Leads
            </button>
            <button
              onClick={() => setActiveTab('google')}
              className={`py-1.5 px-3 border-b-2 font-medium text-xs transition-colors ${
                activeTab === 'google'
                  ? 'border-red-800 text-red-800'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              Google Leads
            </button>
          </nav>
        </div>

        <div className="p-2">
          {/* Filters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Types</option>
              <option value="message">Messages</option>
              <option value="call">Calls</option>
              <option value="lead">Leads</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setServerPage(1); }}
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            >
              <option value="active">Active (New + Follow Up + Existing)</option>
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="follow_up">Follow Up</option>
              <option value="snoozed">Snoozed</option>
              <option value="converted">Converted</option>
              <option value="existing_client_request">Existing Client</option>
              <option value="resolved">Resolved</option>
              <option value="lost">Lost</option>
            </select>

            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Sources</option>
              <option value="google_ads">Google Ads</option>
              <option value="website">Website</option>
              <option value="direct">Direct</option>
            </select>
          </div>

          {/* Archived toggle removed - archiving is batch-only */}

          {/* Empty State */}
          {!loading && filteredAndSortedData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <CheckCircle className="w-12 h-12 mb-3 text-green-300" />
              <p className="text-lg font-medium text-gray-500">All caught up!</p>
              <p className="text-xs text-gray-400 mt-1">No requests match the current filters</p>
            </div>
          )}

          {/* Requests Table */}
          {filteredAndSortedData.length > 0 && (<>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('contact')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Contact</span>
                      {sortField === 'contact' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center gap-1">
                      <span>Created</span>
                      {sortField === 'createdAt' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  {activeTab === 'google' && (
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Conversion</th>
                  )}
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentData.map((request) => (
                  <tr
                    key={request.id}
                    onClick={() => handleViewDetails(request.id)}
                    className={`cursor-pointer ${request.isGoogleAd ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-600' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-2 py-1.5">
                      {request.isGoogleAd ? (
                        <div className="flex items-center gap-1">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-600 text-white text-xs font-bold rounded">
                            ADS
                          </span>
                          <span className="text-xs font-medium text-red-700">{request.typeLabel}</span>
                        </div>
                      ) : (
                        <div className={`flex items-center gap-1 ${getTypeColor(request.type)}`}>
                          {getTypeIcon(request.type)}
                          <span className="text-xs">{request.typeLabel || request.type}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="text-xs font-medium text-gray-900">
                        {request.contact}
                        {request.isExistingClient && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Existing Client
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{request.email}</div>
                      <div className="text-xs text-gray-500">{request.phone}</div>
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="text-xs font-medium text-gray-900">{request.subject}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{request.content}</div>
                      {request.transcript && (
                        <span className="inline-flex items-center text-xs text-blue-600 mt-1">
                          <FileText className="w-3 h-3 mr-1" />
                          Transcript available
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-600">{request.source}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${getStatusColor(request.status)}`}>
                        {request.status === 'existing_client_request' ? 'existing client' : request.status.replace('_', ' ')}
                      </span>
                      {request.status === 'snoozed' && request.snoozeUntil && (
                        <div className="text-xs text-yellow-700 mt-0.5">
                          Until {new Date(request.snoozeUntil + 'T00:00:00').toLocaleDateString()}
                          {request.snoozeComment && (
                            <div className="text-xs text-gray-500 italic truncate max-w-[150px]" title={request.snoozeComment}>
                              {request.snoozeComment}
                            </div>
                          )}
                        </div>
                      )}
                      {request.status === 'follow_up' && request.snoozeComment && (
                        <div className="text-xs text-gray-500 italic truncate max-w-[150px] mt-0.5" title={request.snoozeComment}>
                          {request.snoozeComment}
                        </div>
                      )}
                      {request.status === 'lost' && request.lostReason && (
                        <div className="text-xs text-gray-500 italic truncate max-w-[150px] mt-0.5" title={request.lostReason}>
                          {request.lostReason}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-600">
                      {formatDate(request.createdAt)}
                      {request.duration && (
                        <span className="ml-1 text-gray-400">({Math.floor(request.duration / 60)}:{String(request.duration % 60).padStart(2, '0')})</span>
                      )}
                    </td>
                    {activeTab === 'google' && (
                      <td className="px-2 py-1.5">
                        {request.conversion ? (
                          request.conversion.converted ? (
                            <div>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Converted
                              </span>
                              <div className="text-xs text-gray-600 mt-0.5">
                                {request.conversion.appointments} appt{request.conversion.appointments !== 1 ? 's' : ''} · ${request.conversion.revenue.toLocaleString()}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              Client found
                            </span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-2 py-1.5">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleViewDetails(request.id)}
                          className="text-gray-400 hover:text-red-800 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openActionPopup(request.id, 'snooze'); }}
                          className="text-gray-400 hover:text-yellow-600 transition-colors"
                          title="Snooze"
                        >
                          <Clock className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openActionPopup(request.id, 'resolve'); }}
                          className="text-gray-400 hover:text-teal-600 transition-colors"
                          title="Mark Resolved"
                        >
                          <CheckSquare className="w-3.5 h-3.5" />
                        </button>
                        {request.status !== 'existing_client_request' && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); openActionPopup(request.id, 'converted'); }}
                              className="text-gray-400 hover:text-green-600 transition-colors"
                              title="Won"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); openActionPopup(request.id, 'lost'); }}
                              className="text-gray-400 hover:text-orange-600 transition-colors"
                              title="Lost"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); openActionPopup(request.id, 'delete'); }}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-2 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-700">
                Page {serverPage} of {serverTotalPages} ({totalCount} total)
                {filteredAndSortedData.length < requestsData.length && (
                  <span className="text-gray-500"> · {filteredAndSortedData.length} matching filters</span>
                )}
              </p>
            </div>
            <div className="flex gap-1">
              <Button
                onClick={() => { setServerPage(Math.max(1, serverPage - 1)); setCurrentPage(1); }}
                disabled={serverPage === 1}
                variant={serverPage === 1 ? 'secondary' : 'primary'}
                size="sm"
              >
                ← Prev 50
              </Button>
              <span className="px-2 py-1 text-xs text-gray-600">{serverPage}/{serverTotalPages}</span>
              <Button
                onClick={() => { setServerPage(Math.min(serverTotalPages, serverPage + 1)); setCurrentPage(1); }}
                disabled={serverPage === serverTotalPages || serverTotalPages === 0}
                variant={serverPage === serverTotalPages ? 'secondary' : 'primary'}
                size="sm"
              >
                Next 50 →
              </Button>
            </div>
          </div>
          </>)}
        </div>
      </Panel>

      <div className="pb-16" />

      {/* Request Detail Modal */}
      {selectedInteraction && (
        <RequestDetailModal
          interaction={selectedInteraction}
          onClose={() => setSelectedInteraction(null)}
          onUpdate={handleModalUpdate}
        />
      )}

      {/* Inline Action Popup */}
      {actionPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
          <div className="bg-white border border-gray-200 shadow-lg max-w-sm w-full mx-4">
            <div className={`px-3 py-2 flex items-center justify-between border-b ${
              actionPopup.type === 'delete' ? 'bg-red-800 text-white border-red-900' :
              actionPopup.type === 'converted' ? 'bg-green-700 text-white border-green-800' :
              actionPopup.type === 'lost' ? 'bg-orange-600 text-white border-orange-700' :
              actionPopup.type === 'snooze' ? 'bg-yellow-600 text-white border-yellow-700' :
              actionPopup.type === 'resolve' ? 'bg-teal-600 text-white border-teal-700' :
              'bg-gray-700 text-white border-gray-800'
            }`}>
              <h3 className="text-sm font-semibold">
                {actionPopup.type === 'snooze' && 'Snooze Lead'}
                {actionPopup.type === 'converted' && 'Mark as Won'}
                {actionPopup.type === 'lost' && 'Mark as Lost'}
                {actionPopup.type === 'resolve' && 'Mark as Resolved'}
                {actionPopup.type === 'delete' && 'Delete Lead'}
              </h3>
              <button onClick={closeActionPopup} className="text-white hover:text-gray-200 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 space-y-2">
              {actionPopup.type === 'snooze' ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Follow-up Date</label>
                    <input
                      type="date"
                      value={snoozeDate}
                      onChange={(e) => setSnoozeDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-2 py-1 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Comment / Reminder (optional)</label>
                    <textarea
                      value={snoozeComment}
                      onChange={(e) => setSnoozeComment(e.target.value)}
                      rows={2}
                      className="w-full px-2 py-1 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-800"
                      placeholder="Add a note..."
                    />
                  </div>
                </>
              ) : actionPopup.type === 'lost' ? (
                <>
                  <p className="text-sm text-gray-600">Mark this lead as lost?</p>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
                    <textarea
                      value={lostReason}
                      onChange={(e) => setLostReason(e.target.value)}
                      rows={2}
                      className="w-full px-2 py-1 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-600"
                      placeholder="e.g. Too far, price, no response..."
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  {actionPopup.type === 'converted' && 'Mark this lead as converted (won)?'}
                  {actionPopup.type === 'resolve' && 'Mark this existing client request as resolved (addressed)?'}
                  {actionPopup.type === 'delete' && 'Delete this interaction permanently? This cannot be undone.'}
                </p>
              )}
            </div>

            <div className="bg-gray-50 px-3 py-2 flex justify-end gap-1 border-t border-gray-200">
              <button
                onClick={closeActionPopup}
                disabled={actionProcessing}
                className="px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                disabled={actionProcessing || (actionPopup.type === 'snooze' && !snoozeDate)}
                className={`px-2 py-1 text-xs text-white border transition-colors disabled:opacity-50 ${
                  actionPopup.type === 'delete' ? 'bg-red-800 border-red-900 hover:bg-red-900' :
                  actionPopup.type === 'converted' ? 'bg-green-700 border-green-800 hover:bg-green-800' :
                  actionPopup.type === 'lost' ? 'bg-orange-600 border-orange-700 hover:bg-orange-700' :
                  actionPopup.type === 'resolve' ? 'bg-teal-600 border-teal-700 hover:bg-teal-700' :
                  'bg-red-800 border-red-900 hover:bg-red-900'
                }`}
              >
                {actionProcessing ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RequestsPage; 